import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { PublicUser } from '@tucupet/shared';
import { PageHeader, Table, Btn, Modal, Field, Select, Pager } from '../components/ui';
import { api } from '../lib/api';

type Role = 'client' | 'operator' | 'admin';

export default function UsersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery<{ items: any[]; total: number; page: number; limit: number }>({
    queryKey: ['admin', 'users', search, page],
    queryFn: () => api('/api/admin/users', { query: { search: search || undefined, page } }),
  });
  const [editing, setEditing] = useState<any | null>(null);
  const [adjusting, setAdjusting] = useState<any | null>(null);

  return (
    <div>
      <PageHeader kicker="USUARIOS" title="Cuentas" sub="Roles, baneos y ajustes manuales de billetera." />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ minWidth: 280 }}>
          <Field label="Buscar" value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="email, nombre…" />
        </div>
      </div>

      <Table
        rows={data?.items ?? []}
        empty={isLoading ? 'Cargando…' : 'Sin usuarios'}
        columns={[
          { key: 'name', label: 'Nombre', render: (r: any) => <Link to={`/users/${r.id}`} style={{ color: 'var(--text)', textDecoration: 'none', fontWeight: 600 }}>{r.firstName} {r.lastName}</Link> },
          { key: 'email', label: 'Email', render: (r: any) => <Link to={`/users/${r.id}`} style={{ color: 'var(--text-2)', textDecoration: 'none' }}>{r.email}</Link> },
          { key: 'role', label: 'Rol', render: (r: any) => <span className="mono" style={{ fontSize: 11, color: 'var(--text-2)' }}>{r.role.toUpperCase()}</span> },
          {
            key: 'wallet',
            label: 'Billetera',
            render: (r: any) => (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span className="mono">${r.walletBalance.toFixed(2)}</span>
                {r.walletStatus && r.walletStatus !== 'active' && (
                  <span className="mono" style={{ fontSize: 10, color: 'var(--danger)', letterSpacing: '0.08em' }}>{r.walletStatus.toUpperCase()}</span>
                )}
              </div>
            ),
          },
          { key: 'flags', label: 'Cuenta', render: (r: any) => (r.banned ? <span style={{ color: 'var(--danger)' }}>BANEADO</span> : r.verified ? '✓ Verif.' : 'Sin verificar') },
          {
            key: 'actions',
            label: '',
            render: (r: any) => <UserActions user={r} onChanged={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })} onEdit={() => setEditing(r)} onAdjust={() => setAdjusting(r)} />,
          },
        ]}
      />

      {data && <Pager total={data.total} page={data.page} limit={data.limit} onPage={setPage} />}

      <EditModal open={!!editing} user={editing} onClose={() => setEditing(null)} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })} />
      <AdjustModal open={!!adjusting} user={adjusting} onClose={() => setAdjusting(null)} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })} />
    </div>
  );
}

function UserActions({ user, onChanged, onEdit, onAdjust }: { user: any; onChanged: () => void; onEdit: () => void; onAdjust: () => void }) {
  const isFrozen = user.walletStatus === 'frozen';
  const freeze = useMutation({
    mutationFn: () => api(`/api/admin/users/${user.id}/wallet/freeze`, { body: {} }),
    onSuccess: onChanged,
  });
  const unfreeze = useMutation({
    mutationFn: () => api(`/api/admin/users/${user.id}/wallet/unfreeze`, { body: {} }),
    onSuccess: onChanged,
  });
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <Btn size="sm" variant="ghost" onClick={onEdit}>Editar</Btn>
      <Btn size="sm" variant="ghost" onClick={onAdjust}>Ajustar saldo</Btn>
      {isFrozen ? (
        <Btn size="sm" variant="ghost" onClick={() => unfreeze.mutate()} disabled={unfreeze.isPending}>Descongelar</Btn>
      ) : (
        <Btn size="sm" variant="danger" onClick={() => { if (confirm(`Congelar billetera de ${user.email}? No podrá hacer compras ni recargas hasta descongelarla.`)) freeze.mutate(); }} disabled={freeze.isPending}>Congelar</Btn>
      )}
    </div>
  );
}

function EditModal({ open, user, onClose, onSaved }: { open: boolean; user: any; onClose: () => void; onSaved: () => void }) {
  const [role, setRole] = useState<Role>('client');
  const [banned, setBanned] = useState(false);
  React.useEffect(() => {
    if (user) {
      setRole(user.role);
      setBanned(!!user.banned);
    }
  }, [user]);
  const save = useMutation({
    mutationFn: () => api(`/api/admin/users/${user.id}`, { method: 'PATCH', body: { role, banned } }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Editar ${user?.email ?? ''}`}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={() => save.mutate()} disabled={save.isPending}>Guardar</Btn>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <Select<Role>
          label="Rol"
          value={role}
          onChange={setRole}
          options={[
            { value: 'client', label: 'Cliente' },
            { value: 'operator', label: 'Operator' },
            { value: 'admin', label: 'Admin' },
          ]}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={banned} onChange={(e) => setBanned(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Banear cuenta</span>
        </label>
      </div>
    </Modal>
  );
}

function AdjustModal({ open, user, onClose, onSaved }: { open: boolean; user: any; onClose: () => void; onSaved: () => void }) {
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/api/admin/wallet/adjust`, { body: { userId: user.id, amount, reason } }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Ajustar saldo · ${user?.email ?? ''}`}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={() => save.mutate()} disabled={save.isPending || amount === 0 || reason.length < 3}>Aplicar</Btn>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <Field label="Monto (signed)" value={amount} onChange={(v) => setAmount(parseFloat(v))} type="number" step="0.01" />
        <Field label="Razón" value={reason} onChange={setReason} placeholder="Compensación, reembolso, etc." />
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          Saldo actual: <span className="mono">${user?.walletBalance?.toFixed(2) ?? '0.00'}</span>
        </div>
      </div>
    </Modal>
  );
}
